<template>
  <main class="loyalty-settings-page">
    <section class="hero-card">
      <div>
        <p class="eyebrow">Integracion premium</p>
        <h1>Conexion con Gestion Total</h1>
        <p class="subtitle">
          Configura Afrogato sin archivos .env. Desde aqui se guardan la URL de Gestion Total,
          el token generado en Gestion Total y el token que Gestion Total usara para sincronizar de regreso.
        </p>
      </div>
      <div class="status-pill" :class="statusClass">
        <span class="dot"></span>
        {{ readyLabel }}
      </div>
    </section>

    <section v-if="loading" class="glass-card loading-card">
      Cargando configuracion...
    </section>

    <section v-else class="grid-layout">
      <article class="glass-card form-card">
        <div class="section-head">
          <div>
            <p class="eyebrow">Ajustes principales</p>
            <h2>Datos de conexion</h2>
          </div>
          <button class="ghost-btn" type="button" @click="loadSettings">Actualizar</button>
        </div>

        <label class="field">
          <span>Nombre del sitio</span>
          <input v-model.trim="form.site_name" type="text" placeholder="Afrogato App" />
        </label>

        <label class="field">
          <span>Dominio publico de Afrogato</span>
          <input v-model.trim="form.domain" type="text" placeholder="afrogato.app" />
        </label>

        <label class="field">
          <span>URL de Gestion Total</span>
          <input v-model.trim="form.gt_base_url" type="url" placeholder="https://sistema.gestiontotal.app" />
        </label>

        <div class="token-panel">
          <div>
            <p class="token-title">Token de Gestion Total</p>
            <p class="token-help">
              Pegalo desde Gestion Total &gt; Lealtad &gt; sitio Afrogato &gt; Token de Gestion Total.
              Afrogato lo usa para validar tickets, registrar wallets y emitir canjes. No pegues aqui el token generado por Afrogato.
            </p>
          </div>
          <div class="token-preview">
            <span>{{ connection.has_gt_api_token ? 'Configurado' : 'Pendiente' }}</span>
            <strong>{{ connection.gt_api_token_preview || 'sin token' }}</strong>
          </div>
        </div>

        <label class="field">
          <span>Pegar token generado en Gestion Total</span>
          <input v-model.trim="form.gt_api_token" type="password" autocomplete="off" placeholder="Pega aqui el token GT generado/rotado en Gestion Total" />
          <small>Si lo dejas vacio, se conserva el token actual. Este token debe venir de Gestion Total, no de Afrogato.</small>
        </label>

        <div class="token-panel inbound">
          <div>
            <p class="token-title">Token para recibir sincronizaciones</p>
            <p class="token-help">
              Este token debe copiarse en Gestion Total como token saliente del sitio Afrogato. Sirve cuando el PV asigna puntos o consume un QR.
            </p>
          </div>
          <div class="token-preview">
            <span>{{ connection.has_inbound_sync_token ? 'Configurado' : 'Pendiente' }}</span>
            <strong>{{ connection.inbound_sync_token_preview || 'sin token' }}</strong>
          </div>
        </div>

        <label class="field">
          <span>Token entrante manual</span>
          <input v-model.trim="form.inbound_sync_token" type="password" autocomplete="off" placeholder="Opcional: pegar token propio" />
          <small>Si prefieres, puedes generar uno seguro automaticamente.</small>
        </label>

        <label class="check-row">
          <input v-model="form.generate_inbound_sync_token" type="checkbox" />
          <span>Generar un token nuevo para sincronizacion entrante</span>
        </label>

        <div class="actions-row">
          <button class="primary-btn" type="button" :disabled="saving" @click="saveSettings">
            {{ saving ? 'Guardando...' : 'Guardar configuracion' }}
          </button>
          <button class="secondary-btn" type="button" :disabled="testing || saving" @click="testConnection">
            {{ testing ? 'Probando...' : 'Probar conexion' }}
          </button>
        </div>

        <div v-if="message" class="message" :class="messageType">{{ message }}</div>
      </article>

      <aside class="side-stack">
        <article class="glass-card checklist-card">
          <p class="eyebrow">Estado</p>
          <h2>Checklist</h2>
          <ul>
            <li :class="{ ok: !!form.gt_base_url }"><span></span> URL de Gestion Total</li>
            <li :class="{ ok: connection.has_gt_api_token }"><span></span> Token GT para llamadas salientes</li>
            <li :class="{ ok: connection.has_inbound_sync_token }"><span></span> Token entrante para sync desde PV</li>
            <li :class="{ ok: connection.last_connection_test_status === 'success' }"><span></span> Prueba de conexion exitosa</li>
          </ul>
        </article>

        <article v-if="generatedInboundToken" class="glass-card generated-card">
          <p class="eyebrow">Copia unica</p>
          <h2>Token generado</h2>
          <p>Copialo ahora y pegalo en Gestion Total como token saliente del sitio Afrogato.</p>
          <textarea readonly :value="generatedInboundToken"></textarea>
          <button class="primary-btn full" type="button" @click="copy(generatedInboundToken)">Copiar token</button>
        </article>

        <article class="glass-card instruction-card">
          <p class="eyebrow">Como debe quedar</p>
          <h2>Mapa de configuracion</h2>
          <div class="mini-block">
            <strong>En Afrogato</strong>
            <span>URL GT: https://sistema.gestiontotal.app</span>
            <span>Token GT: el token generado/rotado en Gestion Total</span>
          </div>
          <div class="mini-block">
            <strong>En Gestion Total</strong>
            <span>API base URL: https://afrogato.app</span>
            <span>Token saliente: el token entrante generado aqui</span>
          </div>
        </article>

        <article v-if="connection.last_connection_test_at" class="glass-card last-test-card">
          <p class="eyebrow">Ultima prueba</p>
          <h2 :class="connection.last_connection_test_status">{{ testStatusLabel }}</h2>
          <p>{{ connection.last_connection_test_message }}</p>
          <small>{{ connection.last_connection_test_at }}</small>
        </article>
      </aside>
    </section>
  </main>
</template>

<script>
import {
  getLoyaltyAdminSettings,
  saveLoyaltyAdminSettings,
  testLoyaltyAdminConnection
} from '@/services/menuApi'

export default {
  name: 'LoyaltySettings',
  data() {
    return {
      loading: true,
      saving: false,
      testing: false,
      connection: {},
      generatedInboundToken: '',
      message: '',
      messageType: 'ok',
      form: {
        site_name: 'Afrogato App',
        domain: 'afrogato.app',
        gt_base_url: 'https://sistema.gestiontotal.app',
        gt_api_token: '',
        inbound_sync_token: '',
        generate_inbound_sync_token: false
      }
    }
  },
  computed: {
    readyLabel() {
      return this.connection.ready ? 'Listo para operar' : 'Configuracion pendiente'
    },
    statusClass() {
      return this.connection.ready ? 'ready' : 'pending'
    },
    testStatusLabel() {
      const map = { success: 'Exitosa', warning: 'Con advertencia', failed: 'Fallida' }
      return map[this.connection.last_connection_test_status] || 'Sin prueba'
    }
  },
  mounted() {
    this.loadSettings()
  },
  methods: {
    async loadSettings() {
      this.loading = true
      this.message = ''
      try {
        const data = await getLoyaltyAdminSettings()
        this.connection = data.connection || {}
        this.form.site_name = this.connection.site_name || 'Afrogato App'
        this.form.domain = this.connection.domain || 'afrogato.app'
        this.form.gt_base_url = this.connection.gt_base_url || 'https://sistema.gestiontotal.app'
        this.form.gt_api_token = ''
        this.form.inbound_sync_token = ''
        this.form.generate_inbound_sync_token = false
      } catch (error) {
        this.showMessage(error?.response?.data?.error || 'No se pudo cargar la configuracion.', 'error')
      } finally {
        this.loading = false
      }
    },
    async saveSettings() {
      this.saving = true
      this.message = ''
      this.generatedInboundToken = ''
      try {
        const payload = {
          site_name: this.form.site_name,
          domain: this.form.domain,
          gt_base_url: this.form.gt_base_url,
          gt_api_token: this.form.gt_api_token,
          inbound_sync_token: this.form.inbound_sync_token,
          generate_inbound_sync_token: this.form.generate_inbound_sync_token
        }
        const data = await saveLoyaltyAdminSettings(payload)
        this.connection = data.connection || this.connection
        this.generatedInboundToken = data.generated_inbound_sync_token || ''
        this.form.gt_api_token = ''
        this.form.inbound_sync_token = ''
        this.form.generate_inbound_sync_token = false
        this.showMessage('Configuracion guardada correctamente.', 'ok')
      } catch (error) {
        this.showMessage(error?.response?.data?.error || 'No se pudo guardar la configuracion.', 'error')
      } finally {
        this.saving = false
      }
    },
    async testConnection() {
      this.testing = true
      this.message = ''
      try {
        const data = await testLoyaltyAdminConnection()
        this.connection = data.connection || this.connection
        this.showMessage(data.message || 'Prueba terminada.', data.status === 'failed' ? 'error' : 'ok')
      } catch (error) {
        this.showMessage(error?.response?.data?.message || error?.response?.data?.error || 'No se pudo probar la conexion.', 'error')
      } finally {
        this.testing = false
      }
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.showMessage('Copiado al portapapeles.', 'ok')
      } catch (error) {
        this.showMessage('No se pudo copiar automaticamente. Selecciona y copia el texto manualmente.', 'error')
      }
    },
    showMessage(text, type = 'ok') {
      this.message = text
      this.messageType = type
    }
  }
}
</script>

<style scoped>
.loyalty-settings-page { min-height: 100vh; padding: 96px 18px 42px; background: radial-gradient(circle at 10% 0%, rgba(214,163,75,.24), transparent 28%), radial-gradient(circle at 90% 10%, rgba(64,92,55,.22), transparent 30%), #15170f; color: #f4dfb9; }
.hero-card, .glass-card { max-width: 1180px; margin: 0 auto; border: 1px solid rgba(244,223,185,.16); background: linear-gradient(145deg, rgba(255,255,255,.11), rgba(255,255,255,.045)); box-shadow: 0 24px 70px rgba(0,0,0,.34); backdrop-filter: blur(18px); border-radius: 30px; }
.hero-card { display: flex; justify-content: space-between; gap: 22px; align-items: center; padding: 28px; margin-bottom: 20px; }
.eyebrow { margin: 0 0 8px; color: #d6a34b; text-transform: uppercase; letter-spacing: .16em; font-size: 11px; font-weight: 900; }
h1, h2 { margin: 0; font-family: Georgia, 'Times New Roman', serif; }
h1 { font-size: clamp(32px, 5vw, 58px); line-height: .96; }
h2 { font-size: 24px; }
.subtitle { max-width: 760px; color: rgba(244,223,185,.78); line-height: 1.6; }
.status-pill { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 9px; border-radius: 999px; padding: 12px 16px; font-weight: 900; border: 1px solid rgba(244,223,185,.22); }
.status-pill.ready { background: rgba(75, 166, 99, .16); color: #bff0c7; }
.status-pill.pending { background: rgba(214, 163, 75, .14); color: #ffd995; }
.dot { width: 10px; height: 10px; border-radius: 50%; background: currentColor; box-shadow: 0 0 18px currentColor; }
.grid-layout { max-width: 1180px; margin: 0 auto; display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(320px, .85fr); gap: 20px; align-items: start; }
.glass-card { padding: 22px; }
.loading-card { padding: 34px; text-align: center; }
.section-head { display: flex; justify-content: space-between; gap: 14px; align-items: center; margin-bottom: 18px; }
.field { display: grid; gap: 8px; margin-bottom: 16px; }
.field span, .check-row span { color: rgba(244,223,185,.88); font-weight: 900; }
.field input { width: 100%; box-sizing: border-box; border: 1px solid rgba(244,223,185,.22); background: rgba(0,0,0,.24); color: #f4dfb9; border-radius: 18px; padding: 14px 15px; outline: none; }
.field input:focus { border-color: rgba(214,163,75,.72); box-shadow: 0 0 0 4px rgba(214,163,75,.1); }
.field small { color: rgba(244,223,185,.58); }
.token-panel { display: grid; grid-template-columns: 1fr auto; gap: 14px; align-items: center; border: 1px solid rgba(214,163,75,.22); background: rgba(214,163,75,.08); border-radius: 22px; padding: 16px; margin: 20px 0 14px; }
.token-panel.inbound { border-color: rgba(138,177,115,.28); background: rgba(138,177,115,.08); }
.token-title { margin: 0 0 6px; font-weight: 900; color: #fff4d6; }
.token-help { margin: 0; color: rgba(244,223,185,.66); line-height: 1.5; }
.token-preview { min-width: 150px; border-radius: 18px; background: rgba(0,0,0,.22); padding: 12px; text-align: right; }
.token-preview span { display: block; color: rgba(244,223,185,.58); font-size: 12px; margin-bottom: 4px; }
.token-preview strong { color: #f4dfb9; }
.check-row { display: flex; gap: 10px; align-items: center; margin: 12px 0 20px; }
.check-row input { width: 18px; height: 18px; accent-color: #d6a34b; }
.actions-row { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 22px; }
.primary-btn, .secondary-btn, .ghost-btn { border: 0; border-radius: 999px; padding: 13px 18px; cursor: pointer; font-weight: 900; }
.primary-btn { background: linear-gradient(135deg, #d6a34b, #f2d38a); color: #17120d; box-shadow: 0 12px 28px rgba(214,163,75,.24); }
.secondary-btn, .ghost-btn { border: 1px solid rgba(244,223,185,.22); background: rgba(255,255,255,.06); color: #f4dfb9; }
.primary-btn:disabled, .secondary-btn:disabled { opacity: .55; cursor: not-allowed; }
.full { width: 100%; }
.message { margin-top: 18px; border-radius: 18px; padding: 14px 16px; font-weight: 900; }
.message.ok { background: rgba(75,166,99,.14); color: #bff0c7; border: 1px solid rgba(75,166,99,.25); }
.message.error { background: rgba(196,70,56,.15); color: #ffc6be; border: 1px solid rgba(196,70,56,.28); }
.side-stack { display: grid; gap: 20px; }
.checklist-card ul { list-style: none; display: grid; gap: 12px; padding: 0; margin: 18px 0 0; }
.checklist-card li { display: flex; gap: 10px; align-items: center; color: rgba(244,223,185,.65); font-weight: 800; }
.checklist-card li span { width: 12px; height: 12px; border-radius: 50%; background: rgba(244,223,185,.18); }
.checklist-card li.ok { color: #bff0c7; }
.checklist-card li.ok span { background: #88d68f; box-shadow: 0 0 16px rgba(136,214,143,.6); }
.generated-card textarea { width: 100%; min-height: 96px; resize: vertical; box-sizing: border-box; border-radius: 18px; padding: 12px; background: rgba(0,0,0,.28); border: 1px solid rgba(244,223,185,.2); color: #f4dfb9; margin: 12px 0; }
.instruction-card, .last-test-card { color: rgba(244,223,185,.78); }
.mini-block { display: grid; gap: 7px; border-top: 1px dashed rgba(244,223,185,.18); padding-top: 14px; margin-top: 14px; }
.mini-block strong { color: #f4dfb9; }
.last-test-card h2.success { color: #bff0c7; }
.last-test-card h2.warning { color: #ffd995; }
.last-test-card h2.failed { color: #ffc6be; }
.last-test-card small { color: rgba(244,223,185,.52); }
@media (max-width: 900px) { .hero-card, .grid-layout { display: grid; grid-template-columns: 1fr; } .token-panel { grid-template-columns: 1fr; } .token-preview { text-align: left; } }
</style>
